import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axiosInstance from '../axiosConfig';
import '../assets/CSS/LoopReadinessHeaderShortcut.css';

const LoopReadinessHeaderShortcut = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [access, setAccess] = useState({});
  const [busyKey, setBusyKey] = useState('');
  const [search, setSearch] = useState('');

  const loadReadiness = useCallback(async () => {
    try {
      const accessResponse = await axiosInstance.get('/api/wip/loop-puncture-readiness/access');
      const allowed = Boolean(accessResponse.data?.access?.canAccess);
      setVisible(allowed);
      setAccess(accessResponse.data?.access || {});
      if (!allowed) return;

      const listResponse = await axiosInstance.get('/api/wip/loop-puncture-readiness');
      setRecords(listResponse.data?.records || []);
      setAccess(listResponse.data?.access || accessResponse.data?.access || {});
    } catch (error) {
      setVisible(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReadiness();
    const interval = window.setInterval(loadReadiness, 30000);
    return () => window.clearInterval(interval);
  }, [loadReadiness]);

  const pendingRecords = useMemo(
    () => records.filter(record => record.OverallReadiness !== 'BOTH_READY'),
    [records]
  );
  const canPrepareMaterial = Boolean(access.canConfirmBeltLoop || access.canConfirmPuncture);
  const filteredPendingRecords = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return pendingRecords;
    return pendingRecords.filter(record =>
      String(record.LOT_ID || '').toLowerCase().includes(value)
      || String(record.ScrewProcess || '').toLowerCase().includes(value)
    );
  }, [pendingRecords, search]);

  if (!visible) return null;

  const goToReadiness = () => {
    setOpen(false);
    navigate('/wip-loop-puncture-readiness');
  };

  const confirmFromShortcut = async (record, readinessType, label) => {
    const result = await Swal.fire({
      icon: 'question',
      title: `Confirm ${label} Ready?`,
      html: `<b>LOT: ${record.LOT_ID}</b><br><span style="color:#64748b">Confirm only after the material is physically ready.</span>`,
      showCancelButton: true,
      confirmButtonText: 'Confirm Ready',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a',
    });
    if (!result.isConfirmed) return;

    const key = `${record.ScrewConfirmTimeID}-${readinessType}`;
    setBusyKey(key);
    try {
      const response = await axiosInstance.post(
        `/api/wip/loop-puncture-readiness/${record.ScrewConfirmTimeID}/confirm`,
        { readinessType }
      );
      await loadReadiness();
      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: response.data?.message || `${label} status has been updated.`,
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire('Not Saved', error.response?.data?.message || 'Please try again.', 'error');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <>
      <button
        type="button"
        className="lrh-shortcut"
        onClick={() => { setOpen(true); loadReadiness(); }}
        title="Loop / Puncture Material Readiness"
        aria-label={`Loop / Puncture Material Readiness: ${pendingRecords.length} LOT pending`}
      >
        <span className="lrh-count">{pendingRecords.length}</span>
        <span className="lrh-shortcut-icon">
          <i className={loading ? 'bi bi-arrow-repeat' : 'bi bi-diagram-3-fill'} />
        </span>
      </button>

      {open && (
        <div className="lrh-backdrop" role="presentation" onMouseDown={event => {
          if (event.target === event.currentTarget) setOpen(false);
        }}>
          <section className="lrh-modal" role="dialog" aria-modal="true" aria-labelledby="lrh-title">
            <header className="lrh-modal-header">
              <h2 id="lrh-title">Loop / Puncture Material Readiness</h2>
              <button type="button" className="lrh-close" onClick={() => setOpen(false)} aria-label="Close">
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div className="lrh-modal-body">
              <div className="lrh-filter-row">
                <div className="lrh-search-box">
                  <i className="bi bi-search" />
                  <input
                    type="text"
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder="Filter by Process Name or LOT ID"
                  />
                </div>
                <button type="button" className="lrh-clear-btn" onClick={() => setSearch('')} disabled={!search}>Clear</button>
              </div>

              {filteredPendingRecords.length > 0 ? (
                <div className="lrh-table-wrap">
                  <table className="lrh-table">
                    <thead>
                      <tr>
                        <th>Process Name</th>
                        <th>LOT ID</th>
                        <th>Qty</th>
                        <th>Screw Lot Status</th>
                        <th>Loopi</th>
                        <th>Puncture</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPendingRecords.map(record => (
                        <tr key={record.ScrewConfirmTimeID}>
                          <td>{record.ScrewProcess || '—'}</td>
                          <td><strong>{record.LOT_ID}</strong></td>
                          <td>{Number(record.RequiredQuantity || 0).toLocaleString('en-IN')}</td>
                          <td>
                            <span className={`lrh-status ${String(record.ScrewLotStatus || 'UPCOMING').toLowerCase()}`}>
                              {record.ScrewLotStatus === 'READY' ? 'Ready' : record.ScrewLotStatus === 'BLOCKED' ? 'Blocked' : 'Upcoming'}
                            </span>
                          </td>
                          <td><span className={`lrh-status ${record.BeltLoopStatus === 'READY' ? 'ready' : 'pending'}`}>{record.BeltLoopStatus === 'READY' ? 'Ready' : 'Pending'}</span></td>
                          <td><span className={`lrh-status ${record.PunctureStatus === 'READY' ? 'ready' : 'pending'}`}>{record.PunctureStatus === 'READY' ? 'Ready' : 'Pending'}</span></td>
                          <td>
                            <div className="lrh-lot-actions">
                              {canPrepareMaterial && access.canConfirmBeltLoop && record.BeltLoopStatus !== 'READY' && (
                                <button
                                  type="button"
                                  className="lrh-ready-btn"
                                  disabled={Boolean(busyKey)}
                                  onClick={() => confirmFromShortcut(record, 'BELT_LOOP', 'Belt Loop')}
                                >
                                  <i className="bi bi-check-circle-fill" />
                                  {busyKey === `${record.ScrewConfirmTimeID}-BELT_LOOP` ? 'Please wait...' : 'Loopi Ready'}
                                </button>
                              )}
                              {canPrepareMaterial && access.canConfirmPuncture && record.PunctureStatus !== 'READY' && (
                                <button
                                  type="button"
                                  className="lrh-ready-btn"
                                  disabled={Boolean(busyKey)}
                                  onClick={() => confirmFromShortcut(record, 'PUNCTURE', 'Puncture / Buckle')}
                                >
                                  <i className="bi bi-check-circle-fill" />
                                  {busyKey === `${record.ScrewConfirmTimeID}-PUNCTURE` ? 'Please wait...' : 'Puncture Ready'}
                                </button>
                              )}
                              {!canPrepareMaterial && (
                                <button type="button" className="lrh-view-btn" onClick={goToReadiness}>Open</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="lrh-empty">
                  <i className="bi bi-check-circle" />
                  <strong>{search ? 'No matching LOT found' : 'All material is ready'}</strong>
                </div>
              )}
            </div>

            <footer className="lrh-modal-footer">
              <button type="button" className="lrh-secondary" onClick={() => setOpen(false)}>Close</button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
};

export default LoopReadinessHeaderShortcut;
